import { BaseTenantSearch } from '@/services/_base/BaseTenantSearch.ts'
import { type TransactionRecord } from '@/types/Transaction.ts'
import type { IFirestoreService } from '@/platform/firebase/firestore'

type Session = { tenantId?: string; yardId?: string; userId?: string }

export class TransactionSearch extends BaseTenantSearch<TransactionRecord> {
  constructor(session: Session, firestore: IFirestoreService) {
    super('transaction_records', session, 'transaction.search', firestore)
  }

  /** Prefix search on precomputed lowercased `partyName_lower` */
  prefixPartyName(termRaw: string, limit = 25) {
    const t = termRaw.trim().toLowerCase()
    const end = `${t}\uf8ff`
    this.where('partyName_lower', '>=', t)
      .where('partyName_lower', '<=', end)
      .orderBy('partyName_lower', 'asc')
      .limit(limit)
    this.appendToMetaName('prefix')
    return this
  }

  /** Filter by transaction type */
  byTransactionType(type: 'purchase' | 'sale' | 'transfer' | 'adjustment') {
    this.where('transaction_type', '==', type)
    this.appendToMetaName('byType')
    return this
  }

  /** Filter by transaction status */
  byTransactionStatus(
    status: 'open' | 'pending' | 'submitted' | 'completed' | 'cancelled' | 'editing' | 'deleted',
  ) {
    this.where('transaction_status', '==', status)
    this.appendToMetaName('byStatus')
    return this
  }

  /** Filter by payment status */
  byPaymentStatus(status: 'pending' | 'completed' | 'cancelled') {
    this.where('payment_status', '==', status)
    this.appendToMetaName('byPaymentStatus')
    return this
  }

  /** Exclude completed transactions */
  excludeCompleted() {
    this.where('transaction_status', '!=', 'completed')
    this.appendToMetaName('excludeCompleted')
    return this
  }

  /** Show only completed transactions */
  onlyCompleted() {
    this.where('transaction_status', '==', 'completed')
    this.appendToMetaName('onlyCompleted')
    return this
  }

  /** Show only open transactions (not completed) */
  onlyOpen() {
    this.where('transaction_status', '!=', 'completed')
    this.appendToMetaName('onlyOpen')
    return this
  }

  /** Get recently updated transactions */
  recentlyUpdated(limit = 50) {
    // Ensure updated_at is the only order so required index matches expectations
    this.spec.orders = []
    this.orderBy('updated_at', 'desc')
    this.limit(limit)
    this.appendToMetaName('recentlyUpdated')
    return this
  }

  recent(limit = 20) {
    this.spec.orders = []
    this.orderBy('transaction_date', 'desc')
    this.limit(limit)
    this.appendToMetaName('recent')
    return this
  }

  /** Get transactions by date range */
  byDateRange(startDate: Date, endDate: Date) {
    this.where('transaction_date', '>=', startDate)
      .where('transaction_date', '<=', endDate)
      .orderBy('transaction_date', 'desc')
    this.appendToMetaName('byDateRange')
    return this
  }

  /** Get transactions by party ID */
  byPartyId(partyId: string) {
    this.where('party_id', '==', partyId).orderBy('transaction_date', 'desc')
    this.appendToMetaName('byPartyId')
    return this
  }

  /** Get transactions by person ID */
  byPersonId(personId: string) {
    this.where('personId', '==', personId).orderBy('transaction_date', 'desc')
    this.appendToMetaName('byPersonId')
    return this
  }

  /** Get transactions by organization ID */
  byOrganizationId(organizationId: string) {
    this.where('organizationId', '==', organizationId).orderBy('transaction_date', 'desc')
    this.appendToMetaName('byOrganizationId')
    return this
  }

  /** Get transactions by vehicle ID */
  byVehicleId(vehicleId: string) {
    this.where('vehicleId', '==', vehicleId).orderBy('transaction_date', 'desc')
    this.appendToMetaName('byVehicleId')
    return this
  }

  /** Count open transactions */
  countOpen() {
    this.where('transaction_status', '!=', 'completed')
    this.appendToMetaName('countOpen')
    return this
  }

  /** Count completed transactions */
  countCompleted() {
    this.where('transaction_status', '==', 'completed')
    this.appendToMetaName('countCompleted')
    return this
  }

  /** Count by transaction type */
  countByType(type: 'purchase' | 'sale' | 'transfer' | 'adjustment') {
    this.where('transaction_type', '==', type)
    this.appendToMetaName('countByType')
    return this
  }

  /** Optional: quick paging helpers if/when you add cursors to the spec */
  startAfter(...values: unknown[]) {
    this.cursor('startAfter', ...values)
    return this
  }
}
